import { useEffect, useState } from 'react';
import type { PublicRetroRoom, RetroMode, Role } from '@planning-session/shared';
import { retroAsMarkdown } from '@planning-session/shared';
import { HostControls } from '../HostControls.js';
import { UndoIcon } from '../ui/Icon.js';
import styles from './RetroAside.module.css';

export interface RetroAsideProps {
  readonly room: PublicRetroRoom;
  readonly isHost: boolean;
  readonly isPlayer: boolean;
  onMode: (mode: RetroMode) => void;
  onRole: (role: Role) => void;
  onTransferHost: (participantId: string) => void;
  onRemoveParticipant: (participantId: string) => void;
  onReveal: () => void;
  onUnreveal: () => void;
}

export function RetroAside({ room, isHost, isPlayer, onMode, onRole, onTransferHost, onRemoveParticipant, onReveal, onUnreveal }: RetroAsideProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1_800);
    return () => window.clearTimeout(timer);
  }, [copied]);

  async function copyBoard() {
    try {
      await navigator.clipboard.writeText(retroAsMarkdown(room));
      setCopied(true);
    } catch {
      // Nothing useful to say; the board is on screen.
    }
  }

  // `HostControls` is shared with the planning room and is typed against
  // `PublicParticipant`, which carries two vote-related fields a retro
  // participant has no equivalent of. `HostControls` itself only ever reads
  // `id`, `name` and `connected`, so these are inert padding to satisfy the
  // shared prop type, not meaningful retro state.
  const hostControlsParticipants = room.participants.map((participant) => ({
    ...participant,
    hasVoted: false,
    vote: null,
  }));
  const hasOtherParticipants = room.participants.some((participant) => participant.id !== room.you?.id);

  // Retro has one shortcut, host-only: there is no per-card keyboard vocabulary
  // the way planning's deck gives players "1-9". If that changes, add a
  // player line here the same way SessionAside does.
  const keyHints = isHost ? ['R reveals'] : [];

  return (
    <aside className={styles.aside}>
      <section className={styles.record}>
        <div className={styles.figures}>
          <span className={styles.figure}>
            <span className={styles.figureValue}>{room.notes.length}</span>
            <span className={styles.figureLabel}>{room.notes.length === 1 ? 'Note' : 'Notes'}</span>
          </span>
        </div>

        <button type="button" className={`${styles.copy} ${copied ? styles.copied : ''}`} onClick={copyBoard}>
          {copied ? 'Copied to clipboard' : 'Copy the board'}
        </button>

        {isHost && room.phase === 'writing' && (
          <div className={styles.reveal}>
            <span className={styles.readiness}>
              {room.notes.length === 0
                ? 'Nobody has written anything yet'
                : room.writerCount >= room.playerCount
                  ? 'Everyone has written something'
                  : `${room.writerCount} of ${room.playerCount} have written something`}
            </span>
            <button type="button" className={styles.revealButton} disabled={room.notes.length === 0} onClick={onReveal}>
              {room.notes.length === 0 ? 'Nothing to reveal yet' : room.writerCount >= room.playerCount ? 'Reveal' : 'Reveal anyway'}
            </button>
          </div>
        )}

        {isHost && room.phase === 'revealed' && (
          <div className={styles.revealed}>
            <button type="button" className={styles.backButton} onClick={onUnreveal}>
              <UndoIcon size={14} />
              Go back to writing
            </button>
          </div>
        )}
      </section>

      <div className={styles.controls}>
        <h2 className={styles.controlsTitle}>Settings</h2>

        <div className={styles.control}>
          <span className={styles.controlLabel}>Mode</span>
          {isHost ? (
            <span className={styles.roles} role="group" aria-label="Mode">
              {(['anonymous', 'visible'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`${styles.role} ${room.mode === option ? styles.roleActive : ''}`}
                  aria-pressed={room.mode === option}
                  onClick={() => onMode(option)}
                >
                  {option === 'anonymous' ? 'Anonymous' : 'Visible'}
                </button>
              ))}
            </span>
          ) : (
            <span className={styles.controlValue}>{room.mode === 'anonymous' ? 'Anonymous' : 'Visible'}</span>
          )}
        </div>

        <div className={styles.control}>
          <span className={styles.controlLabel}>You</span>
          <span className={styles.roles} role="group" aria-label="Your role">
            {(['player', 'observer'] as const).map((option) => {
              const active = isPlayer === (option === 'player');
              return (
                <button
                  key={option}
                  type="button"
                  className={`${styles.role} ${active ? styles.roleActive : ''}`}
                  aria-pressed={active}
                  onClick={() => onRole(option)}
                >
                  {option === 'player' ? 'Player' : 'Observer'}
                </button>
              );
            })}
          </span>
        </div>

        {keyHints.length > 0 && (
          <div className={styles.control}>
            <span className={styles.controlLabel}>Keys</span>
            <span className={styles.keys}>{keyHints.join(' · ')}</span>
          </div>
        )}

        {isHost && hasOtherParticipants && (
          <HostControls
            participants={hostControlsParticipants}
            youId={room.you?.id ?? null}
            onTransferHost={onTransferHost}
            onRemoveParticipant={onRemoveParticipant}
          />
        )}
      </div>
    </aside>
  );
}
